import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { BehaviorSubject, Observable } from 'rxjs';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { RegistrationPayload, RegistrationService, saveOnboardingEmail, TenantInfo } from '../registration.service';
import { MatButton } from '@angular/material/button';
import { environment } from '../../environments/environment';
import { applyRegistrationValidationError } from '../registration-validation';

type Step = 'tenantInfo'

interface Status {
  loading: boolean
}

@Component({
    selector: 'app-registration',
    templateUrl: './registration.component.html',
    styleUrls: ['./registration.component.css'],
    standalone: false
})
export class RegistrationComponent {
  private currentStepBs: BehaviorSubject<Step> = new BehaviorSubject<Step>('tenantInfo');
  public currentStep$: Observable<Step> = this.currentStepBs.asObservable();
  public status: Status = { loading: false };

  public userForm!: FormGroup;

  constructor(
    private _fb: FormBuilder,
    private _route: ActivatedRoute,
    private _router: Router,
    private _snackbar: MatSnackBar,
    private _registrationService: RegistrationService,
    private cdr: ChangeDetectorRef) {
  }

  ngOnInit() {
    this.userForm = this._fb.group({
      tenantInfo: null,
    });

    this._route.queryParamMap.subscribe((params: ParamMap) => {
      const tenantId = this.getTenantIdFromParams(params);
      if (!tenantId) {
        return;
      }

      this._router.navigate(['/atlas-signup'], {
        queryParams: { tenantId, step: 'subscription' },
      });
    });
  }

  subformInitialized(name: string, group: FormGroup) {
    this.userForm.setControl(name, group);
    this.cdr.detectChanges();
  }

  submitForm() {
    const formValues = this.userForm.getRawValue();
    const payload = this.buildPayload(formValues);

    this.status.loading = true;
    this._registrationService.register(payload).subscribe({
      next: (response) => {
        this.status.loading = false;
        this._snackbar.open('Check your email to continue.', 'OK');
        const tenantInfo = formValues?.tenantInfo as TenantInfo | undefined;
        const emailAddress = tenantInfo?.emailAddress;
        const tenantId = response?.tenant_name || tenantInfo?.tenantName;
        saveOnboardingEmail(tenantId, emailAddress);
        this._router.navigate(['/thanks'], {
          queryParams: { stage: 'verify-email', tenantId },
          state: tenantInfo ? { tenantInfo } : undefined,
        });
      },
      error: (err) => {
        const reason = applyRegistrationValidationError(this.userForm, err);
        this._snackbar.open(`Registration Failed: ${reason}`, 'FAIL');
        this.status.loading = false;
      },
    });
  }

  buildPayload(formValues: any): RegistrationPayload {
    return {
      tenantInfo: formValues.tenantInfo,
      registrationInfo: {
        provider: "datastorio_saas",
        provider_interface_id: this.getProviderId()
      }
    }
  }

  getProviderId() {
    return environment.env
  }

  private getTenantIdFromParams(params: ParamMap): string {
    return (params.get('tenant_id') ?? params.get('tenantId') ?? '').trim();
  }
}
